/**
 * Friends API Service
 * 
 * Freundschaften und Freundschaftsanfragen
 * 
 * Friends:
 * GET    /friends                              - Freundesliste
 * DELETE /friends/{friend_user_id}             - Freund entfernen
 * 
 * Requests (eingehend):
 * GET    /friends/requests                     - Eingehende Anfragen
 * POST   /friends/requests/{request_id}/accept - Anfrage annehmen
 * POST   /friends/requests/{request_id}/reject - Anfrage ablehnen
 * 
 * Requests (ausgehend):
 * POST   /friends/requests                     - Anfrage senden
 * GET    /friends/requests/outgoing            - Ausgehende Anfragen
 * DELETE /friends/requests/{request_id}        - Anfrage zurückziehen
 * 
 * Friend Data:
 * GET    /friends/{friend_user_id}/library/mangas - Friend Library
 * GET    /friends/{friend_user_id}/stats          - Friend Stats
 */

import { get, post, del, buildCursorParams } from '../client';

// ============================================================================
// FRIENDS LIST
// ============================================================================

/**
 * Freundesliste laden
 * @returns {Promise<{success: boolean, data?: Friend[], error?: object}>}
 * 
 * Response Item:
 * {
 *   user_id: number,
 *   username: string,
 *   avatar_media_key: string
 * }
 */
export const getFriends = async () => {
  return get('/friends');
};

/**
 * Freund entfernen
 * @param {number} friendUserId - User ID des Freundes
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 404 NOT_FOUND
 */
export const removeFriend = async (friendUserId) => {
  return del(`/friends/${friendUserId}`);
};

// ============================================================================
// FRIEND REQUESTS (INCOMING)
// ============================================================================

/**
 * Eingehende Freundschaftsanfragen laden
 * @returns {Promise<{success: boolean, data?: IncomingRequest[], error?: object}>}
 * 
 * Response Item:
 * {
 *   request_id: number,
 *   from_user_id: number,
 *   username: string,
 *   avatar_media_key: string
 * }
 */
export const getIncomingRequests = async () => {
  return get('/friends/requests');
};

/**
 * Freundschaftsanfrage annehmen
 * @param {number} requestId - Request ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 404 NOT_FOUND
 */
export const acceptRequest = async (requestId) => {
  return post(`/friends/requests/${requestId}/accept`);
};

/**
 * Freundschaftsanfrage ablehnen
 * @param {number} requestId - Request ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 404 NOT_FOUND
 */
export const rejectRequest = async (requestId) => {
  return post(`/friends/requests/${requestId}/reject`);
};

// ============================================================================
// FRIEND REQUESTS (OUTGOING)
// ============================================================================

/**
 * Freundschaftsanfrage senden
 * @param {number} userId - User ID des Empfängers
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 409 REQUEST_EXISTS, 409 ALREADY_FRIENDS
 */
export const sendRequest = async (userId) => {
  return post('/friends/requests', { user_id: userId });
};

/**
 * Ausgehende Freundschaftsanfragen laden
 * @returns {Promise<{success: boolean, data?: OutgoingRequest[], error?: object}>}
 * 
 * Response Item:
 * {
 *   request_id: number,
 *   to_user_id: number,
 *   username: string,
 *   avatar_media_key: string
 * }
 */
export const getOutgoingRequests = async () => {
  return get('/friends/requests/outgoing');
};

/**
 * Ausgehende Anfrage zurückziehen
 * @param {number} requestId - Request ID
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 * 
 * Errors: 403 FORBIDDEN, 404 NOT_FOUND
 */
export const cancelRequest = async (requestId) => {
  return del(`/friends/requests/${requestId}`);
};

// ============================================================================
// FRIEND DATA
// ============================================================================

/**
 * Manga-Library eines Freundes laden
 * @param {number} friendUserId - User ID des Freundes
 * @param {object} options - { cursor?, limit? }
 * @returns {Promise<{success: boolean, data?: {items: MangaListItem[], next_cursor: string|null}, error?: object}>}
 * 
 * Errors: 403 PRIVATE_RESOURCE, 404 NOT_FOUND
 */
export const getFriendLibrary = async (friendUserId, options = {}) => {
  const params = buildCursorParams(options);
  return get(`/friends/${friendUserId}/library/mangas`, params);
};

/**
 * Stats eines Freundes laden
 * @param {number} friendUserId - User ID des Freundes
 * @returns {Promise<{success: boolean, data?: FriendStats, error?: object}>}
 * 
 * Response Data:
 * {
 *   manga_count: number,
 *   band_count: number,
 *   read_bands: number,
 *   unread_bands: number
 * }
 * 
 * Errors: 403 PRIVATE_RESOURCE, 404 NOT_FOUND
 */
export const getFriendStats = async (friendUserId) => {
  return get(`/friends/${friendUserId}/stats`);
};

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} Friend
 * @property {number} user_id
 * @property {string} username
 * @property {string} avatar_media_key
 */

/**
 * @typedef {object} IncomingRequest
 * @property {number} request_id
 * @property {number} from_user_id
 * @property {string} username
 * @property {string} avatar_media_key
 */

/**
 * @typedef {object} OutgoingRequest
 * @property {number} request_id
 * @property {number} to_user_id
 * @property {string} username
 * @property {string} avatar_media_key
 */

/**
 * @typedef {object} FriendStats
 * @property {number} manga_count
 * @property {number} band_count
 * @property {number} read_bands
 * @property {number} unread_bands
 */
